import { forwardRef, useMemo, useRef } from 'react';

import { cn, getCurrencySymbol, getNumberFormatter, ia } from 'lib/helpers/utility';

import Hint from '../shared/Hint';
import Typography from '../Typography/Typography';
import InputOptions from '../InputOptions/InputOptions';

import useUnit from './hooks/useUnit';
import useCurrencyValue from './hooks/useCurrencyValue';
import { PERCENT_AMOUNT_DIVISOR, PERCENT_DIVISOR } from './lib/constants';
import { currencyInputStyles, currencyLabelStyles, currencyWrapperStyles } from './lib/styles';

/**
 * @param {import('./lib/propTypes').CurrencyInputProps}
 */

const CurrencyInput = forwardRef(
  (
    {
      variant = 'primary',
      value = 0,
      label,
      onValueChange = () => {},
      parentClassName,
      wrapperClassName,
      disabled,
      required,
      hint,
      error,
      className,
      minValue,
      maxValue,
      currency = 'USD',
      style = 'currency',
      units,
      optionPosition,
      currentUnit,
      onUnitChange = () => {},
      onBlur = () => {},
      textRight = false,
      weight = 'normal',
      textColor = 'neutral-900',
      hideBorder = false,
      hintIcon,
      hintClassName,
      validationMessages,
      labelClassName,
      allowNegative = false,
      decimalEntry = false,
      ...rest
    },
    ref
  ) => {
    const inputRef = useRef(null);
    const isStylePercent = style === 'percent';

    const { inputUnit, handleUnitChange, currentStyle, isPercent } = useUnit({
      style,
      currentUnit,
      value,
      maxValue,
      onUnitChange
    });

    const currentUnitLabel = useMemo(
      () => units?.find((unit) => unit.value === inputUnit)?.label,
      [units, inputUnit]
    );

    const CURRENCY_FORMATTER = useMemo(
      () => getNumberFormatter({ style: currentStyle, currency }),
      [currentStyle, currency]
    );

    const symbol = useMemo(
      () => (isPercent ? '%' : getCurrencySymbol({ currency })),
      [currency, isPercent]
    );

    const {
      inputValue,
      decimalDraft,
      showNegativeSign,
      canBeNegative,
      handleValueChange,
      handleKeyDown,
      handleFocus,
      handleBlur
    } = useCurrencyValue({
      value,
      isPercent,
      isStylePercent,
      currentUnit,
      minValue,
      maxValue,
      symbol,
      validationMessages,
      allowNegative,
      decimalEntry,
      onValueChange,
      onBlur,
      getInput: () => ref?.current || inputRef.current
    });

    const displayAmount = isPercent
      ? inputValue / PERCENT_AMOUNT_DIVISOR
      : inputValue / PERCENT_DIVISOR;
    const formattedValue = canBeNegative
      ? `${showNegativeSign ? '-' : ''}${CURRENCY_FORMATTER.format(Math.abs(displayAmount))}`
      : CURRENCY_FORMATTER.format(displayAmount);
    const currentValue = decimalDraft ?? formattedValue;

    return (
      <div className={cn(disabled && 'cursor-not-allowed', 'flex flex-col gap-2', parentClassName)}>
        {label && (
          <div className={currencyLabelStyles.label}>
            <Typography variant="label" required={required} className={labelClassName}>
              {label}
            </Typography>
          </div>
        )}
        <div
          className={cn(
            currencyWrapperStyles[variant]?.wrapper,
            error ? currencyWrapperStyles[variant]?.error : currencyWrapperStyles[variant]?.default,
            disabled && currencyWrapperStyles[variant]?.disabled,
            hideBorder && currencyWrapperStyles[variant]?.hideBorder,
            wrapperClassName
          )}>
          <input
            ref={ref || inputRef}
            className={cn(
              currencyInputStyles[variant]?.input,
              disabled && currencyInputStyles[variant]?.disabled,
              error
                ? currencyInputStyles[variant]?.inputError
                : currencyInputStyles[variant]?.inputDefault,
              textRight && 'text-right',
              weight && `font-${weight}`,
              textColor && `text-${textColor}`,
              className
            )}
            name="currency-input"
            type="text"
            inputMode="decimal"
            value={currentValue}
            onChange={handleValueChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={`${symbol} 0.00`}
            disabled={disabled}
            aria-invalid={!!error}
            {...rest}
          />

          {ia(units) && (
            <InputOptions
              options={units}
              disabled={disabled}
              position={optionPosition}
              currentOption={currentUnitLabel}
              onClickOption={handleUnitChange}
            />
          )}
        </div>
        <Hint
          hint={hint}
          icon={hintIcon}
          disabled={disabled}
          error={error}
          className={hintClassName}
        />
        {error && (
          <Typography variant="p" size="xs" color="danger-500">
            {error}
          </Typography>
        )}
      </div>
    );
  }
);

export default CurrencyInput;
